import { useCallback, useEffect, useRef, useState } from "react";
import { createVoiceController } from "./index.js";
import type { VoiceController, VoiceControllerOptions, VoiceState } from "./index.js";
import type { UiLanguage } from "./fallback.js";
import type { SttLanguage } from "./stt-client.js";

export interface UseVoiceOptions extends Partial<
  Omit<VoiceControllerOptions, "token" | "language" | "uiLanguage">
> {
  /** Session token (bearer); omit or return null to use the session cookie. */
  token?: string | null;
  /** The user's `voice.language` setting; defaults to the UI language. */
  language?: SttLanguage;
  uiLanguage?: UiLanguage;
  /** Inject a ready controller (tests). */
  controller?: VoiceController;
}

export interface UseVoice {
  state: VoiceState;
  listen(): Promise<string>;
  stopListening(): void;
  speak(text: string): Promise<void>;
  stopSpeaking(): void;
  controller: VoiceController;
}

/**
 * One voice controller per component tree. Token and language are read through refs, so the
 * controller never has to be rebuilt when they change.
 */
export function useVoice(options: UseVoiceOptions = {}): UseVoice {
  const latest = useRef(options);
  latest.current = options;
  const ref = useRef<VoiceController | null>(null);
  if (!ref.current) {
    const { controller, token: _t, language: _l, uiLanguage: _u, ...rest } = options;
    ref.current =
      controller ??
      createVoiceController({
        ...rest,
        token: () => latest.current.token ?? null,
        language: () => latest.current.language ?? latest.current.uiLanguage ?? "auto",
        uiLanguage: () => latest.current.uiLanguage ?? "tr",
      });
  }
  const controller = ref.current;
  const [state, setState] = useState<VoiceState>(controller.state);

  useEffect(() => {
    const off = controller.subscribe(setState);
    void controller.refresh();
    return () => {
      off();
      controller.stopSpeaking();
    };
  }, [controller]);

  const listen = useCallback(() => controller.listen(), [controller]);
  const stopListening = useCallback(() => controller.stopListening(), [controller]);
  const speak = useCallback((text: string) => controller.speak(text), [controller]);
  const stopSpeaking = useCallback(() => controller.stopSpeaking(), [controller]);
  return { state, listen, stopListening, speak, stopSpeaking, controller };
}
